import React from 'react';

import { format, isThisYear } from 'date-fns';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import { ActivtyLog } from 'models/activty-log.model';

import AuditTrailContent from './AuditTrailContent';

type AuditTrailViewProps = {
    sortedDates: string[];
    groupedLogs: Record<string, ActivtyLog[]>;
    showTarget?: boolean;
    getSummary?: (log: ActivtyLog, userLabel: string) => string;
};

/** "Wednesday 7 October", with the year only when it isn't this year. */
const dayHeading = (dateKey: string) => {
    const day = new Date(dateKey);
    if (Number.isNaN(day.getTime())) return dateKey;
    return format(day, isThisYear(day) ? 'EEEE d MMMM' : 'EEEE d MMMM yyyy');
};

const AuditTrailView: React.FC<React.PropsWithChildren<AuditTrailViewProps>> =
    ({ sortedDates, groupedLogs, showTarget, getSummary }) => (
        <Container>
            {sortedDates.map((date) => {
                const count = groupedLogs[date].length;
                return (
                    <Day key={date}>
                        <DayHeader>
                            <DayTitle>{dayHeading(date)}</DayTitle>
                            <DayCount>
                                {count} {count === 1 ? 'change' : 'changes'}
                            </DayCount>
                        </DayHeader>
                        <AuditTrailContent
                            data={groupedLogs[date]}
                            date={date}
                            showTarget={showTarget}
                            getSummary={getSummary}
                        />
                    </Day>
                );
            })}
        </Container>
    );

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

const Day = styled.section({
    display: 'flex',
    flexDirection: 'column',
});

const DayHeader = styled.div({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: aampeTheme.spacing4(3),
    paddingBottom: aampeTheme.spacing4(2),
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
});

const DayTitle = styled.h3({
    margin: 0,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.textAccessibleWG02,
});

const DayCount = styled.span({
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
});

export default AuditTrailView;
